import { Models } from "appwrite";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { checkIsLiked } from "@/lib/utils";
import {
  useLikePost,
  useSavePost,
  useGetCurrentUser,
} from "@/lib/react-query/queries";
import { Button } from "@/components/ui/button";
import { createDealStatus, getDealByBuyerAndSeller, updateDealStatus } from "@/lib/appwrite/api";
import { notificationService } from "@/services/NotificationService";

type PostStatsProps = {
  post: Models.Document;
  userId: string;
  postCreatorId: string;
};

const PostStats = ({ post, userId, postCreatorId }: PostStatsProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // ✅ Fixed: Use the new likedBy array instead of likes relationship
  const likesList = post.likedBy || [];

  const [likes, setLikes] = useState<string[]>(likesList);
  const [isSaved, setIsSaved] = useState(false);
  const [isDealLoading, setIsDealLoading] = useState(false);
  const [isLikeAnimating, setIsLikeAnimating] = useState(false);
  const [isSaveAnimating, setIsSaveAnimating] = useState(false);

  const { mutate: likePost } = useLikePost();
  const { mutate: savePost } = useSavePost();
  const { data: currentUser } = useGetCurrentUser();

  // ✅ Fixed: Check if user saved the post using the new savedBy array
  useEffect(() => {
    if (currentUser && post.savedBy) {
      setIsSaved(post.savedBy.includes(currentUser.$id));
    }
  }, [currentUser, post.savedBy]);

  const handleLikePost = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();
    setIsLikeAnimating(true);

    const newLikes = likes.includes(userId)
      ? likes.filter((id) => id !== userId)
      : [...likes, userId];

    setLikes(newLikes);

    if (!likes.includes(userId)) {
      notificationService.createNotification({
        title: "New Like!",
        body: `${currentUser!.name} liked your post.`,
        type: "like",
        recipient_id: postCreatorId,
        sender_id: userId
      });
    }

    await likePost({ postId: post.$id, likesArray: newLikes });
    
    setTimeout(() => setIsLikeAnimating(false), 600);
  };

  const handleSavePost = (
  e: React.MouseEvent<HTMLButtonElement, MouseEvent>
) => {
  e.stopPropagation();
  setIsSaveAnimating(true);

  // ✅ Fixed: Update savedBy array instead of creating/deleting records
  if (isSaved) {
    // Remove from saved
    setIsSaved(false);
    const newSavedBy = (post.savedBy || []).filter((id: string) => id !== userId);
    savePost({ postId: post.$id, savedByArray: newSavedBy });
  } else {
    // Add to saved
    setIsSaved(true);
    const newSavedBy = [...(post.savedBy || []), userId];
    savePost({ postId: post.$id, savedByArray: newSavedBy });
  }

  setTimeout(() => setIsSaveAnimating(false), 400);
};

  const handleStartDeal = async (e: React.MouseEvent, postCreatorId: string) => {
    e.stopPropagation();
    setIsDealLoading(true);
    
    try {
      const existingDeal = await getDealByBuyerAndSeller(currentUser!.$id, postCreatorId) || 
                          await getDealByBuyerAndSeller(postCreatorId, currentUser!.$id);

      if (!existingDeal) {
        await createDealStatus({
          buyerId: currentUser!.$id,
          sellerId: postCreatorId,
          wasInitiated: true
        });
      } else if (!existingDeal.wasInitiated) {
        await updateDealStatus(existingDeal.$id, {
          wasInitiated: true,
          status: "negotiation",
          requestedAmount: existingDeal.requestedAmount || '0',
          amount: existingDeal.amount || '0'
        });
      }

      navigate(`/chats/${postCreatorId}`);
    } catch (error) {
      console.error("Error starting deal:", error);
    } finally {
      setIsDealLoading(false);
    }
  };

  const containerStyles = location.pathname.startsWith("/profile")
    ? "w-full"
    : "";

  const isLiked = checkIsLiked(likes, userId);

  return (
    <motion.div
      className={`flex justify-between items-center ${containerStyles} px-3 py-3 relative`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Like button - left aligned */}
      <motion.div 
        className="flex items-center gap-2 min-w-0 flex-shrink-0"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <motion.button
          onClick={handleLikePost}
          className="relative p-1.5 rounded-full hover:bg-rose-500/10 transition-colors group flex-shrink-0"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <AnimatePresence mode="wait">
            {isLikeAnimating ? (
              <motion.div
                key="heart-animation"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: [0.8, 1.3, 1], opacity: [0, 1, 1] }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="relative"
              >
                <svg className="w-4 h-4 text-rose-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
                {/* Particles effect */}
                <div className="absolute inset-0">
                  {[...Array(4)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute w-0.5 h-0.5 bg-rose-500 rounded-full"
                      initial={{ 
                        scale: 0, 
                        x: 0, 
                        y: 0,
                        opacity: 1
                      }}
                      animate={{ 
                        scale: [0, 1, 0],
                        x: Math.cos((i * 90) * Math.PI / 180) * 12,
                        y: Math.sin((i * 90) * Math.PI / 180) * 12,
                        opacity: [1, 0.8, 0]
                      }}
                      transition={{ duration: 0.4, delay: 0.05 }}
                    />
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.svg
                key="heart-static"
                className={`w-4 h-4 transition-colors ${
                  isLiked 
                    ? 'text-rose-500 fill-current' 
                    : 'text-light-3 group-hover:text-rose-400'
                }`}
                fill={isLiked ? "currentColor" : "none"}
                stroke="currentColor"
                strokeWidth={isLiked ? 0 : 2}
                viewBox="0 0 24 24"
                initial={false}
                animate={isLiked ? { scale: [1, 1.2, 1] } : { scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </motion.svg>
            )}
          </AnimatePresence>
        </motion.button>
        <motion.p 
          className="text-xs font-medium text-light-1 min-w-[1ch]"
          key={likes.length}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          {likes.length}
        </motion.p>
      </motion.div>

      {/* Start Deal button - center with proper spacing */}
      {post.isSellingPost && (
        <motion.div 
          className="flex-1 flex justify-center px-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Button
            size="sm"
            disabled={isDealLoading}
            className="bg-gradient-to-r from-blue-500/90 to-blue-600/90 hover:from-blue-500 hover:to-blue-600 text-white rounded-full px-3 py-1 text-xs font-medium shadow-sm border-0 transition-all duration-200 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed min-w-[70px] h-7"
            onClick={(e) => handleStartDeal(e, postCreatorId)}
          >
            <AnimatePresence mode="wait">
              {isDealLoading ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-1"
                >
                  <div className="w-2.5 h-2.5 border border-current border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs">Deal...</span>
                </motion.div>
              ) : (
                <motion.div
                  key="start-deal"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-1"
                >
                  <span className="text-xs whitespace-nowrap">Deal</span>
                </motion.div>
              )}
            </AnimatePresence>
          </Button>
        </motion.div>
      )}

      {/* Save button - right aligned */}
      <motion.div
        className="flex-shrink-0"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <motion.button
          onClick={handleSavePost}
          className="relative p-1.5 rounded-full hover:bg-yellow-500/10 transition-colors group"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <AnimatePresence mode="wait">
            {isSaveAnimating ? (
              <motion.div
                key="bookmark-animation"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: [0.8, 1.2, 1], opacity: [0, 1, 1] }}
                exit={{ scale: 0.8, opacity: 0 }}
              >
                <svg 
                  className="w-4 h-4 text-yellow-500" 
                  fill="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z"/>
                </svg>
              </motion.div>
            ) : (
              <motion.svg
                key="bookmark-static"
                className={`w-4 h-4 transition-colors ${
                  isSaved 
                    ? 'text-yellow-500 fill-current' 
                    : 'text-light-3 group-hover:text-yellow-400'
                }`}
                fill={isSaved ? "currentColor" : "none"}
                stroke="currentColor"
                strokeWidth={isSaved ? 0 : 2}
                viewBox="0 0 24 24"
                initial={false}
                animate={isSaved ? { scale: [1, 1.1, 1] } : { scale: 1 }}
                transition={{ duration: 0.2 }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
              </motion.svg>
            )}
          </AnimatePresence>
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

export default PostStats;